import type { RouteLocationNormalized, Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '/@/store/modules/permission';

import { PageEnum } from '/@/enums/pageEnum';
import { useUserStoreWithOut } from '/@/store/modules/user';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';

import { RootRoute } from '/@/router/routes';
import { APP_CODE, TENANT_ID } from '/@/enums/cacheEnum';
import WebStorage from '/@/utils/cache';
import { getMenuList } from '/@/api/sys/menu';
import { useMessage } from '/@/hooks/web/useMessage';

const LOGIN_PATH = PageEnum.BASE_LOGIN;

const ROOT_PATH = RootRoute.path;

const whitePathList: PageEnum[] = [LOGIN_PATH];

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const permissionStore = usePermissionStoreWithOut();

  router.beforeEach(async (to, from, next) => {
    // console.log('to', to);
    // console.log('permissionStore.getIsDynamicAddedRoute', permissionStore.getIsDynamicAddedRoute);
    if (to.fullPath.indexOf('goAppExternal') === -1) {
      const tenantId = WebStorage.get(TENANT_ID);
      const appCode = WebStorage.get(APP_CODE);
      if (redirectIfAppNotAllowed(to, next, permissionStore)) {
        return;
      }
      if (tenantId && appCode && !ifChangeToAnotherApp(to, tenantId, appCode)) {
        if (!to.query.tenantId || !to.query.app) {
          if (to.redirectedFrom) {
            Object.entries(to.redirectedFrom.query).forEach(([key, value]) => {
              to.query[key] = value;
            });
          }
          to.query.tenantId = tenantId;
          to.query.app = appCode;
          next({ ...to, replace: true });
          return;
        }
      } else {
        if (to.query.tenantId && to.query.app) {
          let redirect = `goAppExternal?redirect=${encodeURIComponent(to.fullPath)}`;
          Object.entries(to.query).forEach(([key, value]) => {
            redirect += `&${key}=${value}`;
          });
          next(redirect);
          return;
        } else {
          // 如果本地没有缓存 并且, url里也没有
          // 那么api里读第一个
          const { apps }: any = await getMenuList();
          if (redirectToFirstApp(apps, next)) {
            return;
          }
          next();
          return;
        }
      }
    }

    if (
      from.path === ROOT_PATH &&
      to.path === PageEnum.BASE_HOME &&
      userStore.getUserInfo.homePath &&
      userStore.getUserInfo.homePath !== PageEnum.BASE_HOME
    ) {
      next(userStore.getUserInfo.homePath);
      return;
    }

    const token = userStore.getToken;

    // Whitelist can be directly entered
    if (whitePathList.includes(to.path as PageEnum)) {
      if (to.path === LOGIN_PATH && token) {
        const isSessionTimeout = userStore.getSessionTimeout;
        try {
          await userStore.afterLoginAction();
          if (!isSessionTimeout) {
            next((to.query?.redirect as string) || '/');
            return;
          }
        } catch {}
      }
      next();
      return;
    }
    // token does not exist
    if (!token) {
      // You can access without permission. You need to set the routing meta.ignoreAuth to true
      if (to.meta.ignoreAuth) {
        next();
        return;
      }

      // redirect login page
      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: LOGIN_PATH,
        replace: true,
      };
      if (to.path) {
        redirectData.query = {
          ...redirectData.query,
          redirect: to.path,
        };
      }
      next(redirectData);
      return;
    }

    // Jump to the 404 page after processing the login
    if (
      from.path === LOGIN_PATH &&
      to.name === PAGE_NOT_FOUND_ROUTE.name &&
      to.fullPath !== (userStore.getUserInfo.homePath || PageEnum.BASE_HOME)
    ) {
      next(userStore.getUserInfo.homePath || PageEnum.BASE_HOME);
      return;
    }

    // get userinfo while last fetch time is empty
    if (userStore.getLastUpdateTime === 0) {
      try {
        await userStore.getUserInfoAction();
      } catch (err) {
        console.info(err);
        next();
      }
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }
    const routes = await permissionStore.buildRoutesAction();

    // 请求的应用不在当前账号权限内时，改去权限列表中的第一个应用。
    const resolvedAppCode = WebStorage.get(APP_CODE);
    const requestedAppDenied =
      !!to.query.app && !!resolvedAppCode && to.query.app !== resolvedAppCode;
    if (requestedAppDenied) {
      useMessage().createMessage.warning('当前账号没有该页面的访问权限');
    }

    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });

    router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
    permissionStore.setDynamicAddedRoute(true);

    if (requestedAppDenied && redirectToFirstApp(permissionStore.getAppsList, next)) {
      return;
    }

    if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
      // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
      next({ path: to.fullPath, replace: true, query: to.query });
    } else {
      const redirectPath = (from.query.redirect || to.path) as string;
      const redirect = decodeURIComponent(redirectPath);
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
      next(nextData);
    }
  });
}

function redirectIfAppNotAllowed(
  to: RouteLocationNormalized,
  next: (arg?: any) => void,
  permissionStore: ReturnType<typeof usePermissionStoreWithOut>
): boolean {
  const requestedApp = to.query?.app;
  const apps = permissionStore.getAppsList || [];
  if (!requestedApp || typeof requestedApp !== 'string' || !apps.length) {
    return false;
  }
  // 个人应用在 apps；企业/内部权限在租户自己的 apps 上，不能只拿个人列表判断。
  const tenants = [
    ...(permissionStore.getTenantsList || []),
    ...(permissionStore.getInternalTenantsList || []),
  ];
  const allowed =
    apps.some((app) => app.appCode === requestedApp) ||
    tenants.some((tenant) => tenant.apps?.some((app) => app.appCode === requestedApp));
  if (allowed) {
    return false;
  }
  useMessage().createMessage.warning('当前账号没有该页面的访问权限');
  return redirectToFirstApp(apps, next);
}

/** 没有目标权限时，进入账号权限列表中的第一个应用 */
function redirectToFirstApp(apps: { appCode?: string; url?: string }[] | undefined, next: (arg?: any) => void): boolean {
  const first = apps?.[0];
  if (!first?.url) {
    return false;
  }
  if (first.appCode) {
    WebStorage.set(APP_CODE, first.appCode);
  }
  if (!String(first.url).startsWith('http')) {
    next(first.url);
    return true;
  }
  window.location.href = first.url;
  return true;
}

function ifChangeToAnotherApp(to: RouteLocationNormalized, tenantId: string, app: string): boolean {
  return (
    (to.query.tenantId !== undefined && to.query.tenantId !== tenantId) ||
    (to.query.app !== undefined && to.query.app !== app)
  );
}
